/**
 * DB Commission gallery — CSS Grid masonry (shortest-column, DOM order) + Load more + flat-bottom clip.
 * Scoped: only roots with [data-db-cg-root].
 */
(function () {
  'use strict';

  var SEL_ROOT = '[data-db-cg-root]';
  var BP_TABLET = 768;
  var BP_DESKTOP = 1150;
  var LAYOUT_DEBOUNCE_MS = 80;
  var LAYOUT_DEBOUNCE_RO_MS = 140;
  /** Min measured height (px) before trusting getBoundingClientRect over aspect-ratio fallback */
  var MIN_BOX_HEIGHT = 8;

  function parseBool(s) {
    return s === 'true' || s === true;
  }

  function scheduleLayout(cb) {
    requestAnimationFrame(function () {
      requestAnimationFrame(cb);
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function listItems(list) {
    var out = [];
    var ch = list.children;
    for (var i = 0; i < ch.length; i++) {
      if (ch[i].classList && ch[i].classList.contains('db-commission-gallery__item')) {
        out.push(ch[i]);
      }
    }
    return out;
  }

  function parsePx(val) {
    if (!val || val === 'normal' || val === 'none') return 0;
    var n = parseFloat(String(val));
    return isNaN(n) ? 0 : n;
  }

  function getNumCols(root) {
    var w = window.innerWidth || document.documentElement.clientWidth || 0;
    if (w >= BP_DESKTOP) {
      return Math.max(1, parseInt(root.getAttribute('data-cg-cols-d') || '1', 10) || 1);
    }
    if (w >= BP_TABLET) {
      return Math.max(1, parseInt(root.getAttribute('data-cg-cols-t') || '1', 10) || 1);
    }
    return Math.max(1, parseInt(root.getAttribute('data-cg-cols-m') || '1', 10) || 1);
  }

  function argminCol(nextStart, cols) {
    var mi = 0;
    var mv = nextStart[0];
    for (var i = 1; i < cols; i++) {
      if (nextStart[i] < mv) {
        mv = nextStart[i];
        mi = i;
      }
    }
    return mi;
  }

  function clearPlacement(el) {
    el.style.gridColumn = '';
    el.style.gridRowStart = '';
    el.style.gridRowEnd = '';
    el.removeAttribute('data-db-cg-packed');
    el.removeAttribute('data-db-cg-col');
    el.removeAttribute('data-db-cg-rs');
    el.removeAttribute('data-db-cg-span');
  }

  /**
   * Prefer reserved ratio box height; fall back to data-db-cg-aspect (Shopify width/height) when box not measurable.
   */
  function itemContentHeight(el, colWpx) {
    var ar = parseFloat(el.getAttribute('data-db-cg-aspect') || '0');
    var prev = el.style.width;
    el.style.width = Math.max(0, colWpx) + 'px';
    var h = el.getBoundingClientRect().height;
    el.style.width = prev;

    if (h >= MIN_BOX_HEIGHT) {
      return Math.max(1, h);
    }

    if (ar > 0) {
      var mediaH = colWpx / ar;
      var cap = el.querySelector('.db-commission-gallery__caption');
      var capH = 0;
      if (cap) {
        capH = cap.getBoundingClientRect().height;
        if (!capH || capH < 1) {
          capH = 28;
        }
      }
      return Math.max(1, mediaH + capH);
    }

    return Math.max(1, h || colWpx * 0.72);
  }

  function init(root) {
    if (!root) return;

    if (typeof root._dbCgCleanup === 'function') {
      root._dbCgCleanup();
    }

    var viewport = root.querySelector('[data-db-cg-viewport]');
    var list = viewport && viewport.querySelector('.db-commission-gallery__masonry');
    var btn = root.querySelector('[data-db-cg-load-more]');
    var actions = root.querySelector('[data-db-cg-actions]');

    if (!viewport || !list) return;

    root.setAttribute('data-cg-bound', '1');

    var total = parseInt(root.getAttribute('data-cg-total') || '0', 10) || 0;
    var perLoad = parseInt(root.getAttribute('data-cg-per-load') || '6', 10) || 6;
    var flatBottom = parseBool(root.getAttribute('data-cg-flat-bottom'));
    var designMode = parseBool(root.getAttribute('data-cg-design-mode'));

    var revealed = designMode
      ? total
      : parseInt(root.getAttribute('data-cg-revealed') || '0', 10) || 0;
    if (revealed > total) revealed = total;

    var lastClipHeightPx = null;
    var ro = null;
    var layoutTimer = null;
    var lastLayoutCols = 0;
    var windowLoadHandler = null;
    var roConnected = false;

    function items() {
      return listItems(list);
    }

    function getVisibleItems() {
      var all = items();
      var out = [];
      for (var i = 0; i < all.length && i < revealed; i++) {
        out.push(all[i]);
      }
      return out;
    }

    function applyConcealment() {
      var els = items();
      for (var i = 0; i < els.length; i++) {
        if (designMode || i < revealed) {
          els[i].classList.remove('db-commission-gallery__item--concealed');
        } else {
          els[i].classList.add('db-commission-gallery__item--concealed');
        }
      }
    }

    function readGaps() {
      var st = window.getComputedStyle(list);
      var colGap = parsePx(st.columnGap);
      if (!colGap) colGap = parsePx(st.gap);
      var rowGap = parsePx(st.rowGap);
      if (!rowGap) rowGap = parsePx(st.gap);
      return { colGap: colGap, rowGap: rowGap };
    }

    function layoutMasonry(incremental) {
      var visible = getVisibleItems();
      if (!visible.length) return;

      var cols = getNumCols(root);
      var rowUnit = Math.max(1, parseInt(root.getAttribute('data-cg-row-unit') || '4', 10) || 4);
      var gaps = readGaps();
      var rowGap = gaps.rowGap;
      var rowStride = rowUnit + rowGap;
      if (rowStride < 1) {
        rowStride = 1;
      }

      var inc = !!incremental;
      if (inc && lastLayoutCols > 0 && cols !== lastLayoutCols) {
        inc = false;
      }

      if (inc) {
        for (var x = 0; x < visible.length; x++) {
          if (visible[x].getAttribute('data-db-cg-packed') !== '1') continue;
          var cx = parseInt(visible[x].getAttribute('data-db-cg-col'), 10);
          if (isNaN(cx) || cx < 0 || cx >= cols) {
            inc = false;
            break;
          }
        }
      }

      var nextStart = [];
      var c;
      for (c = 0; c < cols; c++) {
        nextStart[c] = 1;
      }

      if (!inc) {
        for (var k = 0; k < visible.length; k++) {
          clearPlacement(visible[k]);
        }
      }

      var colW = (list.clientWidth - gaps.colGap * Math.max(0, cols - 1)) / Math.max(1, cols);
      if (colW < 1) colW = 1;

      for (var m = 0; m < visible.length; m++) {
        var el = visible[m];

        if (inc && el.getAttribute('data-db-cg-packed') === '1') {
          var c0 = parseInt(el.getAttribute('data-db-cg-col'), 10);
          var rs0 = parseInt(el.getAttribute('data-db-cg-rs'), 10);
          var sp0 = parseInt(el.getAttribute('data-db-cg-span'), 10);
          if (!isNaN(c0) && c0 >= 0 && c0 < cols && !isNaN(rs0) && !isNaN(sp0)) {
            var bottom = rs0 + sp0;
            if (bottom > nextStart[c0]) {
              nextStart[c0] = bottom;
            }
          }
          continue;
        }

        var col = argminCol(nextStart, cols);
        var start = nextStart[col];
        var h = itemContentHeight(el, colW);
        var span = Math.max(1, Math.ceil((h + rowGap) / rowStride));

        el.style.gridColumn = String(col + 1);
        el.style.gridRowStart = String(start);
        el.style.gridRowEnd = 'span ' + span;

        nextStart[col] = start + span;

        el.setAttribute('data-db-cg-packed', '1');
        el.setAttribute('data-db-cg-col', String(col));
        el.setAttribute('data-db-cg-rs', String(start));
        el.setAttribute('data-db-cg-span', String(span));
      }

      lastLayoutCols = cols;
    }

    function scheduleLayoutDebounced(source) {
      if (layoutTimer) {
        clearTimeout(layoutTimer);
      }
      var ms = source === 'ro' ? LAYOUT_DEBOUNCE_RO_MS : LAYOUT_DEBOUNCE_MS;
      layoutTimer = setTimeout(function () {
        layoutTimer = null;
        layoutMasonry(false);
        scheduleLayout(updateClip);
      }, ms);
    }

    function setupResizeObserver() {
      if (roConnected || typeof ResizeObserver === 'undefined') return;
      roConnected = true;
      ro = new ResizeObserver(function () {
        scheduleLayoutDebounced('ro');
      });
      ro.observe(list);
    }

    function attachWindowLoadPass() {
      windowLoadHandler = function () {
        window.removeEventListener('load', windowLoadHandler);
        windowLoadHandler = null;
        if (!root.isConnected) return;
        scheduleLayout(function () {
          layoutMasonry(false);
          updateClip();
        });
      };
      if (document.readyState === 'complete') {
        scheduleLayout(windowLoadHandler);
      } else {
        window.addEventListener('load', windowLoadHandler);
      }
    }

    function markLayoutReady() {
      root.classList.add('db-commission-gallery--layout-ready');
      root.setAttribute('data-cg-ready', 'true');
      if (btn && btn.getAttribute('tabindex') === '-1') {
        btn.removeAttribute('tabindex');
      }
      setupResizeObserver();
    }

    function finishInitialPaint() {
      if (designMode || prefersReducedMotion()) {
        markLayoutReady();
        return;
      }
      requestAnimationFrame(function () {
        markLayoutReady();
      });
    }

    function stripEnterClass(els) {
      for (var i = 0; i < els.length; i++) {
        els[i].classList.remove('db-commission-gallery__item--enter');
      }
    }

    function armEnterAnimation(els) {
      if (!els.length || prefersReducedMotion()) return;
      window.setTimeout(function () {
        stripEnterClass(els);
      }, 520);
    }

    function clearClip() {
      viewport.style.height = '';
      viewport.classList.remove('db-commission-gallery__viewport--clipped');
      lastClipHeightPx = null;
    }

    function updateClip() {
      if (designMode || !flatBottom || revealed >= total) {
        clearClip();
        return;
      }

      var els = items();
      var vis = [];
      for (var i = 0; i < els.length && i < revealed; i++) {
        vis.push(els[i]);
      }
      if (!vis.length) {
        clearClip();
        return;
      }

      var vRect = viewport.getBoundingClientRect();
      var colMax = {};

      for (var j = 0; j < vis.length; j++) {
        var elj = vis[j];
        var cj = parseInt(elj.getAttribute('data-db-cg-col'), 10);
        var r = elj.getBoundingClientRect();
        var relBottom = r.bottom - vRect.top;
        if (!isNaN(cj)) {
          if (!(cj in colMax) || relBottom > colMax[cj]) {
            colMax[cj] = relBottom;
          }
        } else {
          var key = Math.round(r.left);
          if (!(key in colMax) || relBottom > colMax[key]) {
            colMax[key] = relBottom;
          }
        }
      }

      var minBottom = Infinity;
      for (var k in colMax) {
        if (Object.prototype.hasOwnProperty.call(colMax, k)) {
          if (colMax[k] < minBottom) minBottom = colMax[k];
        }
      }

      if (!isFinite(minBottom) || minBottom <= 0) {
        clearClip();
        return;
      }

      var nextH = Math.ceil(minBottom);
      if (lastClipHeightPx === nextH && viewport.classList.contains('db-commission-gallery__viewport--clipped')) {
        return;
      }

      lastClipHeightPx = nextH;
      viewport.style.height = nextH + 'px';
      viewport.classList.add('db-commission-gallery__viewport--clipped');
    }

    function updateActions() {
      if (!actions || !btn) return;
      if (designMode || revealed >= total) {
        actions.setAttribute('hidden', '');
        btn.setAttribute('hidden', '');
      } else {
        actions.removeAttribute('hidden');
        btn.removeAttribute('hidden');
      }
    }

    function onLoadMore() {
      var prevRev = revealed;
      revealed = Math.min(total, revealed + perLoad);
      applyConcealment();
      updateActions();

      var newItems = [];
      var all = items();
      for (var ix = prevRev; ix < revealed && ix < all.length; ix++) {
        newItems.push(all[ix]);
      }

      if (revealed >= total) {
        clearClip();
      }
      scheduleLayout(function () {
        layoutMasonry(true);
        for (var j = 0; j < newItems.length; j++) {
          newItems[j].classList.add('db-commission-gallery__item--enter');
        }
        armEnterAnimation(newItems);
        updateClip();
      });
    }

    function onResize() {
      scheduleLayoutDebounced('resize');
    }

    if (btn) {
      btn.addEventListener('click', onLoadMore);
    }

    window.addEventListener('resize', onResize, { passive: true });

    function destroy() {
      if (layoutTimer) {
        clearTimeout(layoutTimer);
        layoutTimer = null;
      }
      if (windowLoadHandler) {
        window.removeEventListener('load', windowLoadHandler);
        windowLoadHandler = null;
      }
      if (ro) {
        ro.disconnect();
        ro = null;
      }
      roConnected = false;
      window.removeEventListener('resize', onResize);
      if (btn) {
        btn.removeEventListener('click', onLoadMore);
      }
      root.classList.remove('db-commission-gallery--layout-ready');
      root.removeAttribute('data-cg-ready');
      root.removeAttribute('data-cg-bound');
      root._dbCgCleanup = null;
    }

    root._dbCgCleanup = destroy;

    if (btn && actions && !designMode) {
      btn.setAttribute('tabindex', '-1');
    }

    applyConcealment();
    layoutMasonry(false);
    updateActions();
    scheduleLayout(function () {
      updateClip();
      scheduleLayout(finishInitialPaint);
    });

    attachWindowLoadPass();

    if (designMode) {
      setupResizeObserver();
    }
  }

  function boot() {
    var nodes = document.querySelectorAll(SEL_ROOT);
    for (var i = 0; i < nodes.length; i++) {
      init(nodes[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (ev) {
    var t = ev.target;
    if (!t || !t.querySelector) return;
    var root = t.querySelector(SEL_ROOT);
    if (root) {
      init(root);
    }
  });

  document.addEventListener('shopify:section:unload', function (ev) {
    var t = ev.target;
    if (!t || !t.querySelector) return;
    var root = t.querySelector(SEL_ROOT);
    if (root && typeof root._dbCgCleanup === 'function') {
      root._dbCgCleanup();
    }
  });
})();
